import { z } from "zod";
import { ControlExceptionType } from "./attributeTestColumn";

/**
 * The closed vocabularies an analytics column, its verdict and its run manifest
 * speak. They live in one module so the wire schemas, the grid projection and the
 * review pane cannot drift apart on a spelling.
 */

/**
 * What a published workflow output is for.
 *
 * `unclassified` is a terminal value, not a placeholder for "not yet classified":
 * an output the classifier never saw, and one it saw but could not place (a chart
 * has no rows to read a role from), both land here and are excluded from role
 * assignment.
 */
export const analyticsOutputRole = z.enum([
  "population",
  "exceptions",
  "summary",
  "unclassified",
]);

export type AnalyticsOutputRole = z.infer<typeof analyticsOutputRole>;

/** Where the population the workflow ran over came from. */
export const analyticsPopulationSource = z.enum([
  "population_file",
  "import_step",
]);

export type AnalyticsPopulationSource = z.infer<
  typeof analyticsPopulationSource
>;

/** Who last wrote the verdict: the pipeline, or a reviewer overriding it. */
export const analyticsReviewerType = z.enum(["system", "human"]);

export type AnalyticsReviewerType = z.infer<typeof analyticsReviewerType>;

/**
 * A control exception as an analytics verdict carries it.
 *
 * An exception this build cannot name coarsens to the catch-all rather than
 * failing, because the badge reads whether the list is empty and never which
 * members it holds — so the status survives a taxonomy that has grown past this
 * build, where failing would discard the verdict and report a sample carrying
 * findings as untested.
 *
 * The string gate is what stops that tolerance from reaching a malformed
 * element. A value that is not a string is structural damage rather than a
 * category this build has yet to learn, and coarsening it would mint an audit
 * exception the wire never reported; failing it leaves the verdict to be dropped
 * whole, which renders as no answer rather than as a finding.
 *
 * Shared by the verdict and its grid projection so the two cannot disagree about
 * which exceptions a cell holds.
 */
export const analyticsControlException = z
  .string()
  .pipe(ControlExceptionType.catch("general_exception"));

/**
 * Why a verdict no longer stands. `born_superseded` covers a run whose verdict
 * landed after a newer one had already been accepted — superseded from the moment
 * it was written, never the live answer for a moment.
 */
export const analyticsSupersessionReason = z.enum([
  "superseded_by_newer",
  "born_superseded",
]);

export type AnalyticsSupersessionReason = z.infer<
  typeof analyticsSupersessionReason
>;

/** Who chose an input binding. An agent's choice stays human-overridable. */
export const analyticsBoundBy = z.enum(["agent", "human"]);

export type AnalyticsBoundBy = z.infer<typeof analyticsBoundBy>;

/**
 * Lifecycle of a single workflow run, as the domain reports it.
 *
 * `unknown` is not a stage the workflow engine reports — it is what the backend
 * adapter returns for a wire status its table does not recognise, and it is
 * deliberately non-terminal: a state we cannot classify has not been shown to
 * have finished.
 */
export const analyticsRunState = z.enum([
  "pending",
  "running",
  "succeeded",
  "failed",
  "cancelled",
  "unknown",
]);

export type AnalyticsRunState = z.infer<typeof analyticsRunState>;

/**
 * The workflow state of an analytics column, derived by the backend from the
 * binding, the manifest and the workspace's entitlement. Enumerated here so
 * every surface that switches on it shares one set of arms.
 *
 * A status outside this list is a wire contract break, so the schema below
 * refuses it rather than letting a surface fall through to a default arm.
 */
export const WORKFLOW_STATUSES = [
  "not_configured",
  "not_run",
  "invalid_output",
  "ok",
  "workflow_deleted",
  "not_entitled",
] as const;

export type WorkflowStatus = (typeof WORKFLOW_STATUSES)[number];

export const workflowStatus = z.enum(WORKFLOW_STATUSES);

export const INVALID_OUTPUT_CAUSES = [
  "undeclared_key",
  "non_file_key",
  "run_failed",
  "unclassifiable_run_state",
  "unresolved_outputs",
] as const;

export type InvalidOutputCause = (typeof INVALID_OUTPUT_CAUSES)[number];

export const invalidOutputCause = z.enum(INVALID_OUTPUT_CAUSES);
